// #region Imports
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import WorkIcon from "@mui/icons-material/Work";
import {
  type MRT_Row,
} from "material-react-table";
import RowActions, { type RowAction, type RowOpenAction } from "_shared/_views/tables/row-actions";
import { FeeScheduleTemplateLoadIssue, isFeeScheduleTemplateLoadIssue } from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import Guid from "_shared/guids/guid";
// #endregion

// #region Props
type FeeScheduleToolbarProps = {
  row: MRT_Row<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>;
  /** Whether the row is an archived template, listed under the Templates tab's Archived chip (#6716). */
  isArchived: boolean;
  /** Where the row opens: the first item of its ⋯ (#6711). */
  open?: RowOpenAction;
  onArchivedChange: (id: Guid, archived: boolean) => void;
  onCreateProposal?: (template: FeeScheduleTemplate) => void;
  onDelete: (id: Guid) => void;
  /** Opens the template's details, including the proposals and work built from it. */
  onShowDetails?: (template: FeeScheduleTemplate) => void;
};

export default function FeeScheduleToolbar(props: Readonly<FeeScheduleToolbarProps>) {
  const { row, isArchived, open, onArchivedChange, onCreateProposal, onDelete, onShowDetails } = props;

  if (isFeeScheduleTemplateLoadIssue(row.original)) return null;

  const template = row.original;
  const templateName = template.name || "fee schedule template";
  const actions: RowAction[] = [];

  if (onShowDetails) {
    actions.push({
      key: "details",
      label: "Details",
      ariaLabel: `Details of ${templateName}`,
      icon: <InfoIcon />,
      onClick: () => onShowDetails(template),
    });
  }

  if (!isArchived) {
    actions.push(
      {
        key: "create-proposal",
        label: "Create Proposal",
        ariaLabel: `Create proposal from ${templateName}`,
        icon: <WorkIcon />,
        onClick: () => onCreateProposal?.(template),
      },
      {
        key: "delete",
        label: "Delete Template",
        icon: <DeleteIcon />,
        destructive: true,
        onClick: () => {
          if (!template.id) {
            console.warn("Attempting to delete a fee schedule template with no id");
            return;
          }
          onDelete(template.id);
        },
      },
    );
  }

  actions.push({
    key: "archive",
    label: isArchived ? "Unarchive" : "Archive",
    icon: isArchived ? <UnarchiveIcon /> : <ArchiveIcon />,
    onClick: () => {
      if (!template.id) {
        console.warn("Attempting to archive/unarchive a fee schedule template with no id");
        return;
      }
      onArchivedChange(template.id, !isArchived);
    },
  });

  // #region Render
  return <RowActions actions={actions} open={open} ariaLabel={`Actions for ${templateName}`} />;
}
